import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import axios from "axios";
import TradeConfirmModal from "./TradeConfirmModal";
import useUser from "../hooks/useUser";
import ENV from "../config";


dayjs.extend(utc);

// Edit the upgrade wording here.
const UPSELL = {
  title: "Ready to see every trade scored?",
  body: "You've explored your free trades. Unlock unlimited analysis to see every entry and exit scored, and spot the habits costing you money, for about the price of a coffee a month.",
  cta: "See unlimited plans",
};
const PRICING_PATH = "/pricing";

// true  = one free trade per order (a 4-fill order costs 1). Only the first fill's id
//         is sent to /api/deduct-coins/, so the backend must accept that.
// false = one free trade per fill (a 4-fill order costs 4), the old behaviour.
const CHARGE_PER_ORDER = true;
const costOf = (g) => (CHARGE_PER_ORDER ? 1 : g.ids.length);

const inr = (n, digits = 2) =>
  "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: digits });

// executed_at carries a "Z" but already holds exchange (IST) clock time, so format
// it as UTC to show 10:38 instead of shifting it to 16:08.
const fmtExecuted = (s) => dayjs.utc(s).format("DD MMM YYYY, HH:mm");
const uploadDay = (g) => dayjs(g.created_at).format("YYYY-MM-DD");

const SORTERS = {
  symbol: (g) => g.symbol,
  trade_type: (g) => g.trade_type,
  quantity: (g) => g.quantity,
  price: (g) => g.price,
  value: (g) => g.price * g.quantity,
  executed_at: (g) => new Date(g.executed_at).getTime(),
};
const TEXT_COLS = ["symbol", "trade_type"];

// Fills of one order (same symbol, side, exchange, price, minute) become one row.
const mergeFills = (rows = []) => {
  const map = new Map();
  rows.forEach((r) => {
    const minute = dayjs.utc(r.executed_at).format("YYYY-MM-DD HH:mm");
    const key = [r.symbol, r.trade_type, r.exchange, r.price, minute].join("|");
    const g = map.get(key);
    if (g) {
      g.quantity += Number(r.quantity);
      g.ids.push(r.trade_id);
    } else {
      map.set(key, {
        key,
        symbol: r.symbol,
        exchange: r.exchange,
        trade_type: r.trade_type,
        price: Number(r.price),
        quantity: Number(r.quantity),
        executed_at: r.executed_at,
        created_at: r.created_at,
        ids: [r.trade_id],
      });
    }
  });
  return [...map.values()];
};

// Most recent trades first, as many as the limit allows.
const pickRecent = (groups, limit) => {
  const picked = new Set();
  let used = 0;
  [...groups]
    .sort((a, b) => new Date(b.executed_at) - new Date(a.executed_at))
    .forEach((g) => {
      if (used + costOf(g) <= limit) {
        picked.add(g.key);
        used += costOf(g);
      }
    });
  return picked;
};

function SortTh({ col, label, align = "left", orderBy, order, onSort }) {
  const active = orderBy === col;
  return (
    <th
      aria-sort={active ? (order === "asc" ? "ascending" : "descending") : "none"}
      className={`px-4 py-3 text-sm font-medium text-gray-600 ${align === "right" ? "text-right" : "text-left"
        }`}
    >
      <button
        type="button"
        onClick={() => onSort(col)}
        className="inline-flex items-center gap-1 hover:text-gray-900"
      >
        {label}
        <span
          className={`inline-flex items-center ${active ? "text-gray-900" : "text-gray-400"
            }`}
          aria-hidden="true"
        >
          {active ? (
            order === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />
          ) : (
            <ArrowUpDown size={14} />
          )}
        </span>
      </button>
    </th>
  );
}

export default function Table({
  data,
  title = "Choose the trades you want to analyze",
  fromHistory = false,
}) {
  const { user, loading } = useUser();
  const navigate = useNavigate();

  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("executed_at");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const initRef = useRef(null);

  const unlimited = fromHistory || !!user?.has_unlimited_coins;
  const limit = unlimited ? Infinity : (user?.coins ?? 0);

  const groups = useMemo(() => mergeFills(data), [data]);
  const recommended = useMemo(() => pickRecent(groups, limit), [groups, limit]);

  // Pre-select once per upload, after the user (and their coins) have loaded.
  useEffect(() => {
    if (loading || !groups.length || initRef.current === data) return;
    initRef.current = data;
    setSelectedKeys(new Set(recommended));
  }, [data, groups, loading, recommended]);

  const selectedGroups = useMemo(
    () => groups.filter((g) => selectedKeys.has(g.key)),
    [groups, selectedKeys]
  );
  const used = selectedGroups.reduce((s, g) => s + costOf(g), 0);
  const selectedIds = selectedGroups.flatMap((g) => g.ids);
  const chargeIds = selectedGroups.flatMap((g) => (CHARGE_PER_ORDER ? [g.ids[0]] : g.ids));
  const remaining = limit - used;
  const totalCost = groups.reduce((s, g) => s + costOf(g), 0);

  const toggleRow = (g) =>
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(g.key)) next.delete(g.key);
      else if (used + costOf(g) <= limit) next.add(g.key);
      return next;
    });

  const handleSort = (col) => {
    if (orderBy === col) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setOrderBy(col);
      setOrder(TEXT_COLS.includes(col) ? "asc" : "desc");
    }
  };

  const rowsView = useMemo(() => {
    const q = query.trim().toLowerCase();
    const dir = order === "asc" ? 1 : -1;
    const val = SORTERS[orderBy];
    return groups
      .filter((g) => !q || g.symbol.toLowerCase().includes(q))
      .sort((a, b) => {
        if (fromHistory) {
          const d = uploadDay(b).localeCompare(uploadDay(a)); // newest upload first
          if (d) return d;
        }
        const x = val(a);
        const y = val(b);
        const cmp = typeof x === "string" ? x.localeCompare(y) : x - y;
        return cmp * dir || new Date(b.executed_at) - new Date(a.executed_at);
      });
  }, [groups, query, order, orderBy, fromHistory]);

  const pageCount = Math.max(1, Math.ceil(rowsView.length / rowsPerPage));
  const pageRows = rowsView.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handleConfirm = () => {
    const idSet = new Set(selectedIds);
    const selectedRows = data.filter((row) => idSet.has(row.trade_id));
    const tradesBySymbol = selectedRows.reduce((acc, row) => {
      (acc[row.symbol] = acc[row.symbol] || []).push(row);
      return acc;
    }, {});
    navigate("/temp", { state: { tradesBySymbol, fromHistory } });
    setOpen(false);
  };

  const handleConfirmWithCoins = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    const vizId = crypto.randomUUID();
    localStorage.setItem("viz_id", vizId);
    try {
      await axios.post(
        `${ENV.BASE_API_URL}/api/deduct-coins/`,
        { visualization_id: vizId, trade_ids: chargeIds },
        { headers: { Authorization: `Bearer ${localStorage.getItem("jwtToken")}` } }
      );
      handleConfirm();
    } catch (err) {
      console.error("Coin deduction failed", err);
      setOpen(false);
      setError("We couldn't start your analysis. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const handleContinue = () => {
    if (!selectedIds.length) return;
    if (unlimited) handleConfirm();
    else setOpen(true);
  };

  if (!data?.length) return null;

  const finite = Number.isFinite(limit);
  const outOfCoins = finite && limit === 0;
  const capped = finite && !outOfCoins && totalCost > limit;
  const hasQuery = query.trim().length > 0;
  const selectTargets = pickRecent(rowsView, limit);

  const allTargetsSelected =
    selectTargets.size > 0 &&
    [...selectTargets].every((key) => selectedKeys.has(key));

  const selectLabel = allTargetsSelected
    ? "Unselect all"
    : hasQuery
      ? "Select matching"
      : capped
        ? `Select ${limit} most recent`
        : "Select all";
  const lockedHint = (g) =>
    outOfCoins
      ? "Unlock unlimited trades to analyze more"
      : `Needs ${costOf(g)} free trade${costOf(g) === 1 ? "" : "s"}; you have ${remaining} left. Untick another to swap.`;

  const subtitle = fromHistory
    ? "Pick trades from your past uploads to view again."
    : outOfCoins
      ? "You're all caught up on your free trades."
      : finite
        ? `Each trade you analyze uses one free trade. You have ${limit} left.`
        : "Select the trades you'd like scored and plotted on the chart.";

  const sortProps = { orderBy, order, onSort: handleSort };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 pt-10 pb-0">
      <TradeConfirmModal
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirmWithCoins}
        trades={selectedGroups.length}
        coinsUsed={used}
        balance={user?.coins || 0}
        remaining={(user?.coins || 0) - used}
      />

      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">{title}</h1>
        <p className="mt-1.5 text-sm text-gray-500">{subtitle}</p>
      </header>
      {outOfCoins && (
        <div className="relative mb-5 flex flex-col gap-3 overflow-hidden rounded-lg border border-blue-200 bg-blue-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
          {/* Subtle blue accent */}
          <div className="absolute inset-y-0 left-0 w-1 bg-blue-600" />

          <div className="flex min-w-0 flex-1 flex-col gap-1 pl-1 md:flex-row md:items-center md:gap-4">
            <h3 className="shrink-0 text-base font-semibold leading-6 text-gray-900">
              Ready to Go Unlimited?
            </h3>

            <p className="min-w-0 text-sm leading-5 text-gray-700">
              Skip manual logging. Your trades, visualized automatically.
            </p>
          </div>

          <Link
            to={PRICING_PATH}
            className="inline-flex shrink-0 items-center justify-center self-start whitespace-nowrap rounded-md bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 sm:self-auto"
          >
            Unlock Unlimited →
          </Link>
        </div>
      )}

      {capped && (
        <p className="mb-5 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          You uploaded {groups.length} trades. We selected your {used} most recent for your free
          analysis. Tick or untick any row to swap, or{" "}
          <Link to={PRICING_PATH} className="font-medium underline">
            unlock unlimited trades
          </Link>
          .
        </p>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
          placeholder="Search symbol"
          className="w-56 rounded border px-3 py-1.5 text-sm"
        />
        <span className="text-sm text-gray-500">{rowsView.length} trades</span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            disabled={outOfCoins}
            onClick={() =>
              setSelectedKeys(
                allTargetsSelected ? new Set() : selectTargets
              )
            }
            className="rounded border px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            {selectLabel}
          </button>
          <button
            type="button"
            disabled={!selectedKeys.size}
            onClick={() => setSelectedKeys(new Set())}
            className="rounded px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-10 px-4 py-3" />
              <SortTh col="symbol" label="Stock" {...sortProps} />
              <SortTh col="trade_type" label="Type" {...sortProps} />
              <SortTh col="quantity" label="Qty" align="right" {...sortProps} />
              <SortTh col="price" label="Price" align="right" {...sortProps} />
              <SortTh col="value" label="Value" align="right" {...sortProps} />
              <SortTh col="executed_at" label="Executed" {...sortProps} />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageRows.map((g, i) => {
              const isSelected = selectedKeys.has(g.key);
              const disabled = !isSelected && costOf(g) > remaining;
              const newUpload =
                fromHistory && (i === 0 || uploadDay(pageRows[i - 1]) !== uploadDay(g));
              return (
                <React.Fragment key={g.key}>
                  {newUpload && (
                    <tr className="bg-gray-100">
                      <td colSpan={7} className="px-4 py-2 text-sm font-medium text-gray-700">
                        Uploaded on {dayjs(g.created_at).format("D MMM YYYY")}
                      </td>
                    </tr>
                  )}
                  <tr
                    onClick={() => !disabled && toggleRow(g)}
                    title={disabled ? lockedHint(g) : undefined}
                    className={`${isSelected ? "bg-blue-50" : "hover:bg-gray-50"} ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                      }`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={disabled}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleRow(g)}
                        aria-label={`Select ${g.symbol} ${g.trade_type}`}
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">{g.symbol}</div>
                      <div className="text-xs text-gray-500">{g.exchange}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${g.trade_type === "buy"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                          }`}
                      >
                        {g.trade_type === "buy" ? "Buy" : "Sell"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-700">
                      {g.quantity}
                      {g.ids.length > 1 && (
                        <div className="text-xs text-gray-400">{g.ids.length} fills</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-700">{inr(g.price)}</td>
                    <td className="px-4 py-3 text-right text-sm text-gray-700">
                      {inr(g.price * g.quantity, 0)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {fmtExecuted(g.executed_at)}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
            {!pageRows.length && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                  No trades match "{query}".
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm">
        <label>
          Rows per page
          <select
            className="ml-2 rounded border px-2 py-1"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          >
            {[10, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-2">
          <span className="text-gray-500">
            Page {page + 1} of {pageCount}
          </span>
          <button
            className="rounded border px-3 py-1 hover:bg-gray-100 disabled:opacity-40"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(p - 1, 0))}
          >
            Prev
          </button>
          <button
            className="rounded border px-3 py-1 hover:bg-gray-100 disabled:opacity-40"
            disabled={page + 1 >= pageCount}
            onClick={() => setPage((p) => Math.min(p + 1, pageCount - 1))}
          >
            Next
          </button>
        </div>
      </div>

      {/* Sticky action bar: the counter and the button are always in view */}
      <div className="sticky bottom-0 -mx-4 mt-6 flex items-center justify-between gap-4 border-t bg-white/95 px-4 py-3 backdrop-blur">
        <div className="min-w-0 flex-1">
          {outOfCoins ? (
            <div className="text-sm font-medium text-gray-800">Your free trades are in use</div>
          ) : finite ? (
            <>
              <div className="text-sm font-medium text-gray-800">
                {used} of {limit} free trades selected
              </div>
              <div className="mt-1 h-1.5 max-w-xs overflow-hidden rounded bg-gray-200">
                <div
                  className="h-full bg-green-600 transition-all"
                  style={{ width: `${Math.min(100, (used / limit) * 100)}%` }}
                />
              </div>
            </>
          ) : (
            <div className="text-sm font-medium text-gray-800">
              {selectedGroups.length} trades selected
            </div>
          )}
          {error && <div className="mt-1 text-sm text-red-600">{error}</div>}
        </div>
        {outOfCoins ? (
          <Link
            to={PRICING_PATH}
            className="rounded bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700"
          >
            {UPSELL.cta}
          </Link>
        ) : (
          <button
            onClick={handleContinue}
            disabled={!selectedIds.length || busy}
            className="rounded bg-green-600 px-5 py-2.5 font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {selectedGroups.length
              ? `Analyze ${selectedGroups.length} trade${selectedGroups.length === 1 ? "" : "s"}`
              : "Select trades to continue"}
          </button>
        )}
      </div>
    </div>
  );
}